<template>
  <TabelaOSD
    :colunas="displayedColumns"
    :linhas="table.rows"
    :carregando="table.isLoading"
    :total-linhas="table.totalRows"
    :pagina="pagination.page"
    :campo-ordenacao="pagination.sort"
    :ordem-ordenacao="pagination.order"
    :texto-tabela-vazia="$t('indicadoresNaoEncontrados')"
    :campos-modal="obterCamposModal"
    chave-linha="uuid"
    @sort-change="onSortChange"
    @page-change="onPageChange"
    @reload="loadUsuariosData"
  >
    <template #toolbar-actions>
      <UsuariosExport v-bind="pagination" :headers="headers" />
    </template>
    <template #toolbar-filters>
      <UsuariosFilters :loading="table.isLoading" @filter="onFilter" />
    </template>

    <template #cell-perfilUsuario="props">
      <button
        type="button"
        class="btn btn-link p-0 perfil-usuario-link"
        @click.stop="abrirPerfilUsuarioModal(props.linha)"
      >
        {{ props.valor?.nome || '-' }}
        <i class="bi bi-box-arrow-up-right ms-1" />
      </button>
    </template>

    <template #cell-sincronizarTudo="props">
      <span>
        <input
          type="checkbox"
          class="form-check-input"
          :checked="props.linha.sincronizarTudo"
          @change="updateSincronizarTudo(props.linha, $event.target.checked)"
        >
      </span>
    </template>

    <template #cell-ativo="props">
      <span>
        {{ props.linha.ativo ? $t('sim') : $t('nao') }}
      </span>
    </template>
  </TabelaOSD>

  <PerfilUsuarioModal ref="perfilUsuarioModal" />
</template>

<script>
import UsuariosExport from './UsuariosExport.vue';
import UsuariosFilters from './UsuariosFilters.vue';
import TabelaOSD from '@/modules/shared/components/tabelaOSD/TabelaOSD.vue';
import PerfilUsuarioModal from './PerfilUsuarioModal.vue';
import { getPaginatedUsuariosData, AtualizarSincronizarTudo } from '../usuariosService';
import { useToast } from 'vue-toastification';
import { toDate } from '@/modules/core/utils/formats';
import { i18n } from '@/modules/localization';

const HIDDEN_FIELDS = ['emailConfirmado', 'quantidadeReenvioEmail'];

export default {
  components: {
    UsuariosExport,
    UsuariosFilters,
    TabelaOSD,
    PerfilUsuarioModal,
  },

  data() {
    return {
      columns: [
        {
          labelKey: 'nome',
          field: 'nome',
        },
        {
          labelKey: 'email',
          field: 'email',
        },
        {
          labelKey: 'nomeDaEmpresa',
          field: 'empresa',
        },
        {
          labelKey: 'dataDoUltimoLogin',
          field: 'dataHoraUltimoLogin',
          tdClass: 'text-break',
          formatFn: toDate,
        },
        {
          labelKey: 'emailConfirmado',
          field: 'emailConfirmado',
        },
        {
          labelKey: 'quantidadeReenvioEmail',
          field: 'quantidadeReenvioEmail',
        },
        {
          labelKey: 'perfil',
          field: 'perfilUsuario',
          tdClass: 'text-break',
        },
        {
          labelKey: 'ativo',
          field: 'ativo',
        },
        {
          labelKey: 'sincronizarTudo',
          field: 'sincronizarTudo',
          tdClass: 'text-center',
          sortable: false,
        },
      ],
      table: {
        totalRows: 0,
        rows: [],
        isLoading: false,
      },
      pagination: {
        page: 1,
        sort: 'nome',
        order: 'desc',
        search: '',
      },
      toast: useToast(),
    };
  },

  computed: {
    displayedColumns() {
      return this.columns.filter((column) => !HIDDEN_FIELDS.includes(column.field));
    },
    headers() {
      return this.columns.map((column) => this.$t(column.labelKey));
    },
  },

  mounted() {
    this.loadUsuariosData();
  },

  methods: {
    obterCamposModal(linha) {
      return [
        { titulo: this.$t('uuid'), valor: linha.uuid },
        { titulo: this.$t('emailConfirmado'), valor: linha.emailConfirmado ? this.$t('sim') : this.$t('nao') },
        { titulo: this.$t('quantidadeReenvioEmail'), valor: linha.quantidadeReenvioEmail ?? '-' },
      ];
    },

    updateParams(pagination) {
      this.pagination = { ...this.pagination, ...pagination };
    },

    onPageChange(page) {
      this.updateParams({ page });
      this.loadUsuariosData();
    },

    onSortChange(field) {
      const nextOrder = this.pagination.sort === field && this.pagination.order === 'asc' ? 'desc' : 'asc';
      this.updateParams({
        page: 1,
        sort: field,
        order: nextOrder,
      });
      this.loadUsuariosData();
    },

    onFilter(filters) {
      this.updateParams({ ...filters, page: 1 });
      this.loadUsuariosData();
    },

    async loadUsuariosData() {
      this.table.isLoading = true;
      try {
        const data = await getPaginatedUsuariosData(this.pagination);
        this.table.rows = data.dados;
        this.table.totalRows = data.total;
      } catch (error) {
        this.toast.error(i18n.global.t('erroAoObterAsInfoDosUsuarios'));
      } finally {
        this.table.isLoading = false;
      }
    },

    async abrirPerfilUsuarioModal(usuario) {
      if (!usuario.perfilUsuario) return;
      this.$refs.perfilUsuarioModal.show(usuario.perfilUsuario);
    },

    async updateSincronizarTudo(usuario, value) {
      const originalState = usuario.sincronizarTudo;

      try {
        usuario.sincronizarTudo = value;

        await AtualizarSincronizarTudo(usuario);
        this.toast.success(i18n.global.t('usuarioSalvoComSucesso'));
      } catch (error) {
        usuario.sincronizarTudo = originalState;
        this.toast.error(i18n.global.t('falhaAoSalvarUsuario'));
      }
    },
  },
};
</script>

<style scoped>
.perfil-usuario-link {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0d6efd;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
}

.perfil-usuario-link:hover {
  color: #0a58ca;
}
</style>
